import { CommonModule } from '@angular/common';
import {
	Component,
	DestroyRef,
	EventEmitter,
	Input,
	Output,
	Signal,
	computed,
	effect,
	inject,
	signal,
	untracked
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	FormBuilder,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	Validators
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { LucideAngularModule } from 'lucide-angular';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EMPTY, catchError, debounceTime, finalize, of, switchMap, tap } from 'rxjs';
import {
	CallejeroResult,
	CallejeroService
} from '../../../../core/services/callejero.service';
import { PARTIDOS } from '../../../../shared/statics/partidos';

type Partido = {
	id: number;
	description: string;
};

@Component({
	selector: 'app-user-settings-address',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatCardModule,
		MatSelectModule,
		MatTableModule,
		LucideAngularModule,
		MatButtonModule,
		MatTooltipModule,
		MatAutocompleteModule,
		MatProgressSpinnerModule
	],
	templateUrl: './user-settings_address.html',
	styleUrl: './user-settings_address.scss'
})
export class UserSettingsAddressComponent {
	@Input({ required: true }) requestForm!: Signal<boolean>;
	@Input({ required: true }) addressesData!: Signal<any[]>;
	@Input() modoEdicion = false;
	@Input() soloLectura = true;

	mostrarFormAgregar = signal(false);

	@Output() addressesChange = new EventEmitter<any[]>();

	private fb = inject(FormBuilder);
	private destroyRef = inject(DestroyRef);
	private callejeroService = inject(CallejeroService);

	addressForm: FormGroup;

	tiposDomicilio = [
		{ id: '1', description: 'Registrado' },
		{ id: '2', description: 'Real' },
		{ id: '3', description: 'Legal' },
		{ id: '4', description: 'Laboral' }
	];

	tiposDomicilioFiltered = signal(this.tiposDomicilio);

	addresses = signal<any[]>([]);
	originalAddresses = signal<any[]>([]);

	dataSource = new MatTableDataSource<any>();
	displayedColumns: string[] = [
		'tipoDomicilio',
		'calle',
		'numero',
		'localidad',
		'partido',
		'codPostal',
		'accion'
	];

	modoEdicionDomicilio = signal(false);
	domicilioAEditarIndex = signal<number | null>(null);
	tipoDomicilioAEditar = signal<string | null>(null);

	showHasChanges = signal(false);

	callejeroSugerencias = signal<CallejeroResult[]>([]);
	callejeroValidado = signal(false);
	callejeroError = signal(false);
	isLoadingCallejero = signal(false);

	readonly partidos: Partido[] = PARTIDOS;
	readonly partidoBusqueda = signal('');
	readonly isVicenteLopez = signal(false);

	readonly partidosFiltrados = computed(() => {
		const value = this.partidoBusqueda().toLowerCase();
		return value
			? this.partidos.filter((p) => p.description.toLowerCase().includes(value))
			: this.partidos.slice(0, 20);
	});

	private selectedLat = signal('');
	private selectedLon = signal('');
	private selectedCalle = signal('');
	private selectedNumero = signal('');
	private selectingFromCallejero = false;
	private lastSelectedDireccion = '';

	displayDireccion = (value: CallejeroResult | string | null): string => {
		if (!value) return '';
		if (typeof value === 'string') return value;
		const road = value.address?.road || value.display_name?.split(', ')[0]?.trim() || '';
		const num = value.address?.house_number || '';
		return num ? `${road} ${num}` : road;
	};

	displayPartido = (partido: Partido | null): string => partido?.description ?? '';

	constructor() {
		this.addressForm = this.createForm();

		effect(() => {
			const data = this.addressesData();
			if (!data) return;

			this.addresses.set([...data]);
			this.originalAddresses.set([...data]);
			this.dataSource.data = [...data];
			untracked(() => this.filtrarOpcionesTipoDomicilio());
			this.showHasChanges.set(false);
		});

		effect(() => {
			if (this.requestForm()) {
				this.sendFormData();
			}
		});

		this.addressForm.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.checkForChanges());

		this.addressForm.get('partidoBusqueda')!.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((value) => {
				this.partidoBusqueda.set(
					typeof value === 'string' ? value : (value?.description ?? '')
				);
			});

		const direccionControl = this.addressForm.get('direccion')!;

		direccionControl.valueChanges
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				tap((val) => {
					if (this.selectingFromCallejero) return;
					if (val !== null && typeof val !== 'string') return;
					this.lastSelectedDireccion = '';
					if (this.callejeroValidado()) {
						this.callejeroValidado.set(false);
						this.selectedLat.set('');
						this.selectedLon.set('');
						this.selectedCalle.set('');
						this.selectedNumero.set('');
					}
					this.callejeroError.set(false);
				}),
				debounceTime(500),
				switchMap(() => {
					if (!this.isVicenteLopez()) {
						return EMPTY;
					}

					const val = direccionControl.value;
					if (val !== null && typeof val !== 'string') return EMPTY;
					const query = typeof val === 'string' ? val.trim() : '';

					if (query === this.lastSelectedDireccion || query.length < 5) {
						this.callejeroSugerencias.set([]);
						return EMPTY;
					}

					this.isLoadingCallejero.set(true);
					return this.callejeroService.search(query).pipe(
						finalize(() => this.isLoadingCallejero.set(false)),
						catchError(() => of([]))
					);
				})
			)
			.subscribe((results) => this.callejeroSugerencias.set(results));
	}

	private createForm(): FormGroup {
		return this.fb.group({
			tipoDomicilio: ['', Validators.required],
			partido: [null, Validators.required],
			partidoBusqueda: [null, Validators.required],
			direccion: [{ value: '', disabled: true }, [Validators.required, Validators.minLength(5)]],
			localidad: ['', [Validators.required, Validators.minLength(2)]],
			codPostal: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]],
			torre: [''],
			piso: [''],
			depto: ['']
		});
	}

	onSeleccionarPartido(partido: Partido): void {
		const isVL = partido.description === 'VICENTE LÓPEZ';
		this.isVicenteLopez.set(isVL);
		this.addressForm.get('direccion')!.enable();
		if (!isVL) {
			this.callejeroSugerencias.set([]);
		}
		this.addressForm.patchValue({
			partido: partido.description,
			partidoBusqueda: partido
		});
	}

	onSeleccionarSugerencia(result: CallejeroResult): void {
		const parts = result.display_name.split(', ');
		const numero = result.address?.house_number || '';
		const calle = result.address?.road || parts[0]?.trim() || '';
		const localidad = result.address?.city_district || parts[1]?.trim() || '';

		this.selectedCalle.set(calle);
		this.selectedNumero.set(numero);
		this.lastSelectedDireccion = numero ? `${calle} ${numero}` : calle;

		this.addressForm.patchValue({ localidad });
		this.callejeroValidado.set(true);
		this.callejeroError.set(false);
		this.selectedLat.set(result.lat);
		this.selectedLon.set(result.lon);
		this.callejeroSugerencias.set([]);
	}

	filtrarOpcionesTipoDomicilio(): void {
		const ids = this.addresses().map((d) => d.tipoDomicilio);
		const tipoEdit = this.tipoDomicilioAEditar();

		this.tiposDomicilioFiltered.set(
			this.tiposDomicilio.filter((t) => !ids.includes(t.id) || t.id === tipoEdit)
		);
	}

	getTipoDomicilio(tipoId: string): string {
		return this.tiposDomicilio.find((t) => t.id === tipoId)?.description ?? 'Desconocido';
	}

	agregarNuevoDomicilio(): void {
		if (this.addressForm.invalid) {
			this.addressForm.markAllAsTouched();
			return;
		}

		const v = this.addressForm.getRawValue();

		let calle = this.selectedCalle();
		let numero = this.selectedNumero();
		if (!this.callejeroValidado()) {
			const partes = String(v.direccion).trim().split(/\s+/);
			numero = /^\d+$/.test(partes[partes.length - 1]) ? partes.pop()! : '';
			calle = partes.join(' ');
		}

		const nuevo = {
			tipoDomicilio: v.tipoDomicilio,
			calle,
			numero,
			localidad: v.localidad,
			partido: v.partido,
			cp: String(v.codPostal),
			torre: v.torre || '',
			piso: v.piso || '',
			depto: v.depto || ''
		};

		const updated = [...this.addresses(), nuevo];
		this.addresses.set(updated);
		this.dataSource.data = updated;

		this.mostrarFormAgregar.set(false);
		this.showHasChanges.set(true);
		this.filtrarOpcionesTipoDomicilio();
		this.emitAddressesChange();
		this.resetFormState();
	}

	editarDomicilio(domicilio: any, index: number): void {
		this.modoEdicionDomicilio.set(true);
		this.domicilioAEditarIndex.set(index);
		this.tipoDomicilioAEditar.set(domicilio.tipoDomicilio);

		this.selectingFromCallejero = true;

		const isVL = domicilio.partido === 'VICENTE LÓPEZ';
		const partidoObj = this.partidos.find((p) => p.description === domicilio.partido) ?? null;

		this.isVicenteLopez.set(isVL);
		this.addressForm.get('direccion')!.enable();

		const direccion = domicilio.numero
			? `${domicilio.calle} ${domicilio.numero}`
			: domicilio.calle;

		this.addressForm.patchValue({
			tipoDomicilio: domicilio.tipoDomicilio,
			partido: domicilio.partido,
			partidoBusqueda: partidoObj,
			direccion,
			localidad: domicilio.localidad,
			codPostal: domicilio.cp,
			torre: domicilio.torre || '',
			piso: domicilio.piso || '',
			depto: domicilio.depto || ''
		});

		this.selectedCalle.set(domicilio.calle || '');
		this.selectedNumero.set(domicilio.numero || '');
		this.callejeroValidado.set(false);
		this.selectedLat.set('');
		this.selectedLon.set('');
		this.callejeroError.set(false);
		this.callejeroSugerencias.set([]);
		this.addressForm.get('tipoDomicilio')?.disable();
		this.filtrarOpcionesTipoDomicilio();

		setTimeout(() => {
			this.selectingFromCallejero = false;
		}, 0);
	}

	guardarEdicionDomicilio(): void {
		if (this.addressForm.invalid || this.domicilioAEditarIndex() === null) {
			this.addressForm.markAllAsTouched();
			return;
		}

		const index = this.domicilioAEditarIndex()!;
		const v = this.addressForm.getRawValue();

		let calle = this.selectedCalle();
		let numero = this.selectedNumero();
		if (!this.callejeroValidado()) {
			const partes = String(v.direccion).trim().split(/\s+/);
			numero = /^\d+$/.test(partes[partes.length - 1]) ? partes.pop()! : '';
			calle = partes.join(' ');
		}

		const updated = [...this.addresses()];
		updated[index] = {
			...updated[index],
			tipoDomicilio: v.tipoDomicilio,
			calle,
			numero,
			localidad: v.localidad,
			partido: v.partido,
			cp: v.codPostal,
			torre: v.torre || '',
			piso: v.piso || '',
			depto: v.depto || ''
		};

		this.addresses.set(updated);
		this.dataSource.data = updated;

		this.cancelarEdicion();
		this.showHasChanges.set(true);
		this.emitAddressesChange();
	}

	eliminarDomicilio(index: number): void {
		if (!confirm('¿Está seguro que desea eliminar este domicilio?')) return;

		const updated = this.addresses().filter((_, i) => i !== index);
		this.addresses.set(updated);
		this.dataSource.data = updated;

		this.showHasChanges.set(true);
		this.filtrarOpcionesTipoDomicilio();
		this.emitAddressesChange();
	}

	cancelarEdicion(): void {
		this.addressForm.get('tipoDomicilio')?.enable();
		this.modoEdicionDomicilio.set(false);
		this.mostrarFormAgregar.set(false);
		this.domicilioAEditarIndex.set(null);
		this.tipoDomicilioAEditar.set(null);
		this.filtrarOpcionesTipoDomicilio();
		this.resetFormState();
	}

	resetForm(): void {
		if (!this.showHasChanges()) return;

		if (confirm('¿Está seguro que desea descartar los cambios?')) {
			const original = [...this.originalAddresses()];
			this.addresses.set(original);
			this.dataSource.data = original;
			this.showHasChanges.set(false);
			this.emitAddressesChange();
			this.resetFormState();
		}
	}

	private resetFormState(): void {
		this.isVicenteLopez.set(false);
		this.addressForm.reset();
		this.addressForm.get('direccion')!.disable();
		this.callejeroValidado.set(false);
		this.callejeroError.set(false);
		this.callejeroSugerencias.set([]);
		this.selectedLat.set('');
		this.selectedLon.set('');
		this.selectedCalle.set('');
		this.selectedNumero.set('');
	}

	private checkForChanges(): void {
		this.showHasChanges.set(
			JSON.stringify(this.addresses()) !== JSON.stringify(this.originalAddresses())
		);
	}

	private sendFormData(): void {
		if (this.addresses().length === 0) return;
		this.emitAddressesChange();
	}

	private emitAddressesChange(): void {
		this.addressesChange.emit(this.addresses());
	}
}
